"use client";
import { useEffect, useMemo, useState } from "react";

import { PRODUCT_INDEX_EN } from "@/constants/algolia";
import getPublicClient from "@/lib/algolia/getPublicClient";
import { ProductType as CourseType } from "@/common/course/types";
import logger from "@/lib/logger";
import { getFacetAttributeName } from "@/components/Search/utils/getFacetAttributeName";
import { AlgoliaHit } from "@/components/ProductSearch/types";

const getHits = async (client: any, searchParams: { [key: string]: any }) => {
  const params = { ...searchParams };
  const { hits, queryID } = await client.searchSingleIndex({
    indexName: PRODUCT_INDEX_EN,
    searchParams: params,
  });
  return hits.map((hit: AlgoliaHit) => ({ ...hit, queryID }));
};

const processAutocompleteHits = (productHits: AlgoliaHit[]) => {
  // we need to filter out potential duplicates if some EE or Boot Camps products were returned in both queries
  const executiveEducation = productHits
    .filter((hit) => hit.product == CourseType.EXECUTIVE_EDUCATION)
    .slice(0, 2);

  const generalHits = productHits.filter(
    (hit) => hit.product != CourseType.EXECUTIVE_EDUCATION,
  );
  const processedHits = [...executiveEducation, ...generalHits];
  return processedHits.slice(0, 10);
};

const handleError = (error: Error) => {
  const message = `Failed to fetch products for autocomplete`;
  logger.error(message, error);
};

const useAutocomplete = (query: string) => {
  const client = getPublicClient();
  const [productHits, setProductHits] = useState<AlgoliaHit[]>([]);
  const indexName = PRODUCT_INDEX_EN as string;

  useEffect(() => {
    if (query) {
      const searchParams = {
        query,
        clickAnalytics: true,
        restrictSearchableAttributes: [
          getFacetAttributeName("title", { indexName }), //TODO: Add productName as searchable attribute for product summary index
        ],
        attributesToRetrieve: [
          getFacetAttributeName("title", { indexName }),
          getFacetAttributeName("marketing_url", { indexName }),
          getFacetAttributeName("subject", { indexName }),
          getFacetAttributeName("partner_keys", { indexName }),
          getFacetAttributeName("uuid", { indexName }),
          "product",
          getFacetAttributeName("program_type", { indexName }),
        ],
        highlightPreTag: `<em class="bg-transparent font-bold not-italic">`,
        highlightPostTag: "</em>",
        hitsPerPage: 250,
      };
      const getProductHits = async () => {
        const hits = await getHits(client, searchParams);
        setProductHits(hits);
      };

      getProductHits().catch((e) => handleError(e));
    }
  }, [query, client, indexName]);

  const processedHits = useMemo(
    () => processAutocompleteHits(productHits),
    [productHits],
  );

  return processedHits;
};

export default useAutocomplete;
